/* eslint-disable react/prop-types */
import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  makeStyles,
} from '@material-ui/core';
import { mdiEye } from '@mdi/js';
import Icon from '@mdi/react';
import ActionContainer from 'components/ActionContainer/ActionContainer';
import Button from 'components/Button/Button';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import Loading from 'components/Loading/Loading';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getActivityLogsForImage } from 'store/activityLog/services/getActivityLogsForImage.service';
import { selectUserActiveEmployerInfo } from 'store/user/selectors.js/user.selectors';
import formatDurationBetween from 'utils/formatDurationBetween';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    ...theme.component.dialogPaper,
  },
  dialogActions: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
  viewTimeRow: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  empty: {
    color: theme.palette.text.secondary,
  },
}));

/**
 * Lists who has viewed an image. Fetches every VIEW_IMAGE activity-log row for the
 * file permission (via the gsi2 target endpoint) and dedupes to distinct viewers —
 * each an openable ActionContainer titled with their most-recent view time (and,
 * when repeated, a view count) that expands to every view timestamp.
 * Local state only (self-contained; no redux slice needed).
 */
const WhoViewedImageDialog = ({
  open,
  onClose,
  projectId,
  filePermissionId,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('common');
  const activeEmployerInfo = useSelector(selectUserActiveEmployerInfo);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (open && projectId && filePermissionId) {
      setLoading(true);
      setError(false);
      setItems([]);
      getActivityLogsForImage({ projectId, filePermissionId, activeEmployerInfo })
        .then((data) => {
          if (!cancelled) setItems(data?.items || []);
        })
        .catch(() => {
          if (!cancelled) setError(true);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [open, projectId, filePermissionId, activeEmployerInfo]);

  // Keep only view events, then collapse to distinct viewers. Items arrive
  // newest-first, so each viewer's views list is newest-first and views[0]
  // is their latest view.
  const viewers = useMemo(() => {
    const byUser = new Map();
    (items || [])
      .filter((item) => item.action === 'VIEW_IMAGE')
      .forEach((item) => {
        const key = item.userId || item.userName;
        if (!key) return;
        let viewer = byUser.get(key);
        if (!viewer) {
          viewer = { key, userName: item.userName, views: [] };
          byUser.set(key, viewer);
        }
        viewer.views.push(item);
      });
    return [...byUser.values()];
  }, [items]);

  return (
    <Dialog
      open={open}
      classes={{ paper: classes.dialogPaper }}
      onClose={onClose}
      aria-labelledby="who-viewed-image-dialog"
      maxWidth="xs"
      fullWidth
    >
      <DialogHeader
        onClose={onClose}
        title={t('activity_log.viewed_by', 'Viewed by')}
      />
      <DialogContent>
        {loading ? (
          <Loading tiny />
        ) : error ? (
          <p className={classes.empty}>
            {t('activity_log.viewers_error', "Couldn't load viewers")}
          </p>
        ) : viewers.length > 0 ? (
          viewers.map((viewer) => (
            <ActionContainer
              key={viewer.key}
              title={`${
                viewer.userName ||
                t('activity_log.unknown_user', 'Unknown user')
              } · ${moment(viewer.views[0].createdAt).format('l HH:mm')}${
                viewer.views.length > 1
                  ? ` · ${viewer.views.length} ${t(
                      'activity_log.views',
                      'views'
                    )}`
                  : ''
              }`}
              titleIcon={<Icon path={mdiEye} size={1} />}
              closable
            >
              {viewer.views.map((view, index) => (
                <Grid
                  item
                  xs={12}
                  key={view.sk || index}
                  className={classes.viewTimeRow}
                >
                  {moment(view.createdAt).format('l HH:mm')}
                  {view.endedAt
                    ? ` · ${formatDurationBetween(
                        view.createdAt,
                        view.endedAt
                      )}`
                    : ''}
                </Grid>
              ))}
            </ActionContainer>
          ))
        ) : (
          <p className={classes.empty}>
            {t('activity_log.no_views', 'No one has viewed this image yet')}
          </p>
        )}
      </DialogContent>
      <Divider />
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} color="dark">
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhoViewedImageDialog;
